import { useState, useEffect, useRef } from 'react'
import { VitalSignsList } from './VitalSignsList'
import { UnitPreference } from '../types/vitalSigns'
import { getUnitPreference } from '../utils/userPreferences'
import './VitalSignsTableDialog.css'

interface VitalSignsTableDialogProps {
  patientId: string
  consultationId?: string
  userPreference?: UnitPreference
  isOpen: boolean
  onClose: () => void
}

export function VitalSignsTableDialog({
  patientId,
  userPreference,
  isOpen,
  onClose,
}: VitalSignsTableDialogProps) {
  const actualPreference = userPreference || getUnitPreference()
  const [position, setPosition] = useState({ x: window.innerWidth - 900, y: 50 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const widgetRef = useRef<HTMLDivElement>(null)

  // Handle drag
  useEffect(() => {
    if (!isDragging || !widgetRef.current) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.x
      const deltaY = e.clientY - dragStart.y
      const widgetWidth = 900
      const widgetHeight = window.innerHeight * 0.9
      const maxY = Math.max(0, window.innerHeight - widgetHeight)
      setPosition((prev) => ({
        x: Math.max(0, Math.min(window.innerWidth - widgetWidth, prev.x + deltaX)),
        y: Math.max(0, Math.min(maxY, prev.y + deltaY)),
      }))
      setDragStart({ x: e.clientX, y: e.clientY })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart])

  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.vital-signs-table-dialog-header')) {
      setIsDragging(true)
      setDragStart({ x: e.clientX, y: e.clientY })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={widgetRef}
      className="vital-signs-table-dialog"
      style={{
        position: 'fixed',
        right: `${window.innerWidth - position.x - 900}px`,
        top: `${position.y}px`,
        zIndex: 1001,
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="vital-signs-table-dialog-expanded">
        <div
          className="vital-signs-table-dialog-header"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <h3>Vital Signs Table</h3>
          <button
            className="vital-signs-table-dialog-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>
        <div className="vital-signs-table-dialog-body">
          <VitalSignsList
            patientId={patientId}
            userPreference={actualPreference}
          />
        </div>
      </div>
    </div>
  )
}
